/**
 * /careers — public careers page.
 *
 * 2026-08-26 (founder ask) — deliberately has NO list of open roles. The whole
 * page funnels into one always-open general application which POSTs to
 * /api/landing/apply/; that endpoint stores the row AND emails the founder with
 * reply_to set to the applicant, so a CV lands in the inbox either way.
 *
 * Reuses LandingNavbar / LandingFooter from Home.jsx so the page reads as part
 * of the marketing site rather than as an app screen — hence the raw black +
 * #e53935 Tailwind classes here instead of the app's bg-bg-* theme tokens.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, Send } from "lucide-react";
import api from "../../api/axios";
import { LandingFooter, LandingNavbar } from "./Home";

const CAREERS_EMAIL = "a.manivannan@sportingzenminds.com";

// Must stay in sync with landing.models.AREA_CHOICES on the backend — the
// `value` is what gets stored, and an unknown value would render as blank in
// the admin changelist.
const AREAS = [
  { value: "engineering", label: "Engineering", desc: "React + Django, real-time scoring, video pipelines, offline-first sync." },
  { value: "data", label: "Data / Analytics", desc: "Ball-by-ball modelling, scrapers, canonical player data, match archives." },
  { value: "scoring", label: "Cricket scoring & ops", desc: "Score matches, run tournament coverage, keep the data honest." },
  { value: "design", label: "Design", desc: "Product surfaces a scorer can drive one-handed at a noisy ground." },
  { value: "sales", label: "Sales / Partnerships", desc: "Associations, leagues, academies, broadcasters." },
  { value: "content", label: "Content / Media", desc: "Match reports, social, and the cricket writing around the numbers." },
  { value: "internship", label: "Internship", desc: "Students and freshers — engineering, data or scoring." },
  { value: "other", label: "Something else", desc: "Tell us what you would do here that we have not thought of." },
];

const WHY = [
  { title: "Small team, real ownership", desc: "You will own a surface end to end — model, endpoint, screen, and the customer using it. Nothing here is a ticket queue." },
  { title: "AI in the actual product", desc: "Claude reads match video and pre-fills runs, extras, shot type and line/length. It is not a chatbot bolted on the side — it is in the scoring loop." },
  { title: "Cricket, every day", desc: "Ball-by-ball data, wagon wheels, pitch maps, press reports. If you follow the game, the domain is the fun part." },
  { title: "Shipped, not staged", desc: "Work goes live to scorers, analysts and associations quickly. You will see your change used in a real match." },
];

const PROCESS = [
  { num: "1", title: "You apply", desc: "The form below, or an email with your CV. Either reaches the same inbox." },
  { num: "2", title: "We read it", desc: "Every application gets read by a human. We aim to reply within a week." },
  { num: "3", title: "A conversation", desc: "A 30-minute call about what you have built and what you would want to build here." },
  { num: "4", title: "A real task", desc: "A short, scoped piece of work close to the job — not a whiteboard puzzle." },
];

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-200 mb-1.5">
        {label}
        {required && <span className="text-[#e53935] ml-0.5">*</span>}
      </span>
      {children}
      {hint && <span className="block text-xs text-gray-500 mt-1">{hint}</span>}
    </label>
  );
}

const inputCls =
  "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm " +
  "placeholder-gray-500 focus:outline-none focus:border-[#e53935]/60 focus:bg-white/[0.07] transition-colors";

export default function Careers() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", location: "",
    area: "", experience: "", resume_url: "", portfolio_url: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Clicking an area card both selects it and drops the visitor at the form,
  // so the "where might I fit" section doubles as the form's first question.
  const pickArea = (value) => {
    update("area", value);
    document.getElementById("apply")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/api/landing/apply/", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      // 429 = the endpoint's 5/hour anon throttle. Say so plainly rather than
      // showing DRF's raw "Request was throttled" wording.
      const status = err?.response?.status;
      setError(
        status === 429
          ? `Too many submissions from this network. Please email your CV to ${CAREERS_EMAIL} instead.`
          : err?.response?.data?.error || err?.message || "Something went wrong — please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="font-sans antialiased bg-black min-h-screen">
      <LandingNavbar anchorPrefix="/" />

      {/* HERO */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 bg-gradient-to-b from-zinc-950 to-black overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center px-3 py-1 rounded-full border border-[#e53935]/40 bg-[#e53935]/10 text-[#e53935] text-xs font-bold tracking-widest uppercase mb-6">
            Careers · Chennai, India
          </span>
          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight leading-tight">
            Help us build the <br className="hidden sm:block" />
            <span className="text-[#e53935]">data layer for cricket.</span>
          </h1>
          <p className="text-lg text-gray-400 leading-relaxed max-w-2xl mx-auto mb-8">
            We hire year-round rather than posting roles one at a time. If you are good
            and the work below sounds like yours, send us your CV — we read every one.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="#apply"
              className="w-full sm:w-auto bg-[#e53935] hover:bg-red-700 text-white font-semibold px-7 py-3 rounded-lg transition-colors"
            >
              Send us your CV
            </a>
            <a
              href={`mailto:${CAREERS_EMAIL}?subject=Application%20%E2%80%94%20ZenCricketData`}
              className="w-full sm:w-auto border border-white/20 hover:border-white/40 text-gray-200 hover:text-white font-medium px-7 py-3 rounded-lg transition-colors"
            >
              Or email it directly
            </a>
          </div>
        </div>
      </section>

      {/* WHAT WE ARE BUILDING */}
      <section className="relative py-16 lg:py-20 bg-black border-t border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 tracking-tight">
            What we are building
          </h2>
          <div className="space-y-4 text-gray-400 leading-relaxed">
            <p>
              <span className="text-white font-semibold">Zenminds Sporting Technologies</span> makes
              ZenCricketData — the platform cricket scorers, analysts, academies and associations use
              to capture every delivery of a match and turn it into something useful.
            </p>
            <p>
              A scorer records a ball. That single ball becomes a live scorecard, a wagon wheel, a pitch
              map, an opposition dossier, a press report written by Claude, a row in an API a broadcaster
              is pulling from — and, over a season, the record of a player&apos;s career. Most cricket played
              in this country still leaves no data behind at all. We are changing that, one ground at a time.
            </p>
            <p>
              Alongside the product we run two marketplaces —{" "}
              <Link to="/find-a-scorer" className="text-[#e53935] hover:underline">find a scorer</Link> and{" "}
              <Link to="/find-a-player" className="text-[#e53935] hover:underline">find a player</Link> — and a
              historical match archive that keeps growing.
            </p>
          </div>
        </div>
      </section>

      {/* WHY WORK HERE */}
      <section className="relative py-16 lg:py-20 bg-gradient-to-b from-black to-zinc-950 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 tracking-tight text-center">
            Why work here
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {WHY.map((w) => (
              <div key={w.title} className="glass-effect border border-white/10 rounded-2xl p-6 hover:border-[#e53935]/30 transition-colors">
                <h3 className="text-white font-bold text-lg mb-2">{w.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHERE YOU MIGHT FIT */}
      <section className="relative py-16 lg:py-20 bg-black border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
              Where you might fit
            </h2>
            <p className="text-gray-400">
              These are not posted vacancies — they are the parts of the company that grow.
              Pick the closest one and it will carry into your application.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {AREAS.map((a) => (
              <button
                key={a.value}
                type="button"
                onClick={() => pickArea(a.value)}
                className={`text-left glass-effect border rounded-2xl p-5 transition-colors ${
                  form.area === a.value
                    ? "border-[#e53935] bg-[#e53935]/10"
                    : "border-white/10 hover:border-[#e53935]/40"
                }`}
              >
                <h3 className="text-white font-semibold mb-1.5">{a.label}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{a.desc}</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* NOT A FULL-TIME ROLE — route scorers/players to the marketplaces
          instead of losing them to the application form. */}
      <section className="relative py-16 bg-zinc-950 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-effect border border-white/10 rounded-2xl p-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Not looking for a full-time job?
            </h2>
            <p className="text-gray-400 mb-6 max-w-2xl mx-auto leading-relaxed">
              If you are a scorer who wants paid tournament work, or a player who wants to be
              found by scouts and academies, you do not need to apply here — list yourself on the
              marketplace instead.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/find-a-scorer" className="w-full sm:w-auto bg-white/10 hover:bg-white/15 border border-white/15 text-white font-medium px-6 py-2.5 rounded-lg transition-colors">
                Join the scorer network
              </Link>
              <Link to="/find-a-player" className="w-full sm:w-auto bg-white/10 hover:bg-white/15 border border-white/15 text-white font-medium px-6 py-2.5 rounded-lg transition-colors">
                List yourself as a player
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* HIRING PROCESS */}
      <section className="relative py-16 lg:py-20 bg-black border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 tracking-tight text-center">
            How hiring works here
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS.map((p) => (
              <div key={p.num} className="relative">
                <div className="w-10 h-10 rounded-full bg-[#e53935] text-white font-bold flex items-center justify-center mb-4">
                  {p.num}
                </div>
                <h3 className="text-white font-bold mb-2">{p.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section id="apply" className="relative py-16 lg:py-24 bg-gradient-to-b from-black to-zinc-950 border-t border-white/5 scroll-mt-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {done ? (
            <div className="glass-effect border border-emerald-500/30 rounded-2xl p-8 text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center justify-center mx-auto mb-4">
                <Check className="w-6 h-6" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">Got it — thank you.</h2>
              <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                Your application is with us and a human will read it. We aim to reply within a week,
                whether or not it is a fit. If you want to add anything, just write to{" "}
                <a href={`mailto:${CAREERS_EMAIL}`} className="text-[#e53935] hover:underline">{CAREERS_EMAIL}</a>.
              </p>
              <Link to="/" className="inline-flex items-center gap-1 text-[#e53935] hover:underline text-sm">
                Back to home
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
                  Send us your CV
                </h2>
                <p className="text-gray-400">
                  Two fields are required. Everything else helps, none of it is a filter.
                </p>
              </div>

              <form onSubmit={submit} className="glass-effect border border-white/10 rounded-2xl p-6 sm:p-8 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Your name" required>
                    <input className={inputCls} required value={form.name}
                           onChange={(e) => update("name", e.target.value)} placeholder="Full name" />
                  </Field>
                  <Field label="Email" required>
                    <input type="email" className={inputCls} required value={form.email}
                           onChange={(e) => update("email", e.target.value)} placeholder="you@example.com" />
                  </Field>
                  <Field label="Phone">
                    <input className={inputCls} value={form.phone}
                           onChange={(e) => update("phone", e.target.value)} placeholder="+91…" />
                  </Field>
                  <Field label="Where you are based">
                    <input className={inputCls} value={form.location}
                           onChange={(e) => update("location", e.target.value)} placeholder="Chennai, remote…" />
                  </Field>
                  <Field label="Area you are applying for">
                    <select className={inputCls} value={form.area}
                            onChange={(e) => update("area", e.target.value)}>
                      <option value="" className="bg-zinc-900">Select an area…</option>
                      {AREAS.map((a) => (
                        <option key={a.value} value={a.value} className="bg-zinc-900">{a.label}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Experience">
                    <input className={inputCls} value={form.experience}
                           onChange={(e) => update("experience", e.target.value)} placeholder="Fresher, 3 years, 10+ years…" />
                  </Field>
                </div>

                <Field
                  label="Link to your CV"
                  hint={`Google Drive, Dropbox or LinkedIn — anything we can open. No link? Email the file to ${CAREERS_EMAIL} and mention your name here.`}
                >
                  <input type="url" className={inputCls} value={form.resume_url}
                         onChange={(e) => update("resume_url", e.target.value)}
                         placeholder="https://drive.google.com/…" />
                </Field>

                <Field label="Portfolio / GitHub / anything you have built">
                  <input type="url" className={inputCls} value={form.portfolio_url}
                         onChange={(e) => update("portfolio_url", e.target.value)}
                         placeholder="https://github.com/…" />
                </Field>

                <Field label="Why here?" hint="A few honest lines beat a cover letter.">
                  <textarea rows={5} className={inputCls} value={form.message}
                            onChange={(e) => update("message", e.target.value)}
                            placeholder="What you would want to work on, and anything about you the CV does not say." />
                </Field>

                {error && (
                  <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#e53935] hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-lg transition-colors inline-flex items-center justify-center gap-2"
                >
                  {submitting
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                    : <><Send className="w-4 h-4" /> Send application</>}
                </button>

                <p className="text-xs text-gray-500 text-center">
                  We use what you send here only to consider you for a role. Nothing goes to anyone else.
                </p>
              </form>
            </>
          )}
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
